import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import FormField from "./form-field";
import { Link } from "react-router-dom"; // Keep Link for "Already have an account?"
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function RegisterForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const [email, setEmail] = useState("");
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleMagicLinkSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setIsSendingLink(true);
    setError(null);
    try {
      // For registration, the process is the same: initiate magic link.
      // The backend will handle new user creation if the email isn't found.
      const { error: magicLinkError } = await authClient.signIn.magicLink({
        email,
        callbackURL: `${window.location.origin}/dashboard`, // Or a specific post-registration/login page
      });

      if (magicLinkError) {
        setError(magicLinkError.message || t("registerForm.magicLinkError"));
        toast.error(magicLinkError.message || t("registerForm.magicLinkError"));
      } else {
        toast.success(t("registerForm.magicLinkSuccess"));
        // Optionally, clear the email field or show a message to check email
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error(message);
    } finally {
      setIsSendingLink(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>{t("registerForm.magicLinkTitle")}</CardTitle>
          <CardDescription>
            {t("registerForm.magicLinkDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleMagicLinkSubmit}>
            <div className="flex flex-col gap-4">
              <FormField
                label={t("common.email")}
                id="email"
                type="email"
                placeholder={t("common.emailPlaceholder")}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSendingLink}
              />
              {error && (
                <p className="mt-2 text-sm text-destructive">{error}</p>
              )}
              <div className="flex flex-col gap-3">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSendingLink}
                >
                  {isSendingLink
                    ? t("registerForm.sendingLink")
                    : t("registerForm.sendMagicLink")}
                </Button>
              </div>
            </div>
            <div className="mt-4 text-center text-sm">
              {t("registerForm.loginPrompt")}{" "}
              <Link to="/login" className="underline underline-offset-4">
                {t("registerForm.loginLink")}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
